/* ============================================================================
   Curated Collection JS - Custom Theme Version
   Pins a specific product/variant to first position in collection
============================================================================ */

(() => {
  const GRID_SEL = '.product-grid';

  // Parse URL parameters
  const qp = new URLSearchParams(location.search);

  let rawFirst = decodeURIComponent((qp.get('first') || '').trim());
  let firstHandle = '';
  let variantFromURL = '';

  // Extract handle from URL or direct handle
  if (rawFirst.startsWith('http')) {
    try {
      const u = new URL(rawFirst);
      const segs = u.pathname.split('/').filter(Boolean);
      if (segs[0] === 'products' && segs[1]) firstHandle = segs[1];
      variantFromURL = u.searchParams.get('variant') || '';
    } catch (_) {}
  } else {
    const segs = rawFirst.split('/').filter(Boolean);
    firstHandle = segs.pop();
  }

  if (!firstHandle) return; // Exit if no product to pin

  // Get variant ID (digits only)
  const variantID = (/^\d+$/.test(qp.get('variant') || variantFromURL))
    ? (qp.get('variant') || variantFromURL)
    : '';

  // Get source collection
  let source = (qp.get('source') || '').trim();
  if (!source) {
    const p = location.pathname.split('/').filter(Boolean);
    if (p[0] === 'collections' && p[1]) source = p[1];
  }
  if (!source) return;

  // Get limit
  const limit = Math.max(1, parseInt(qp.get('limit') || '16', 10));

  const grid = document.querySelector(GRID_SEL);
  if (!grid) return;

  // Theme editor preview detection
  const previewExtras = (() => {
    const list = [];
    ['preview_theme_id', 'preview_theme_token', 'pb'].forEach(k => {
      if (qp.has(k)) list.push(`${k}=${encodeURIComponent(qp.get(k) || '')}`);
    });
    return list.join('&');
  })();
  const appendPreview = url =>
    previewExtras ? (url.includes('?') ? `${url}&${previewExtras}` : `${url}?${previewExtras}`) : url;

  /* ------------------------------------------------------------------ */
  /* Main Logic: Reorder products from collection                      */
  /* ------------------------------------------------------------------ */
  (async () => {
    try {
      // Find if the pinned product already exists in the grid
      const existingItems = Array.from(grid.querySelectorAll('.product-grid__item'));
      let pinnedItem = null;
      
      // Check each product card to find matching handle
      for (const item of existingItems) {
        const links = item.querySelectorAll('a[href*="/products/"]');
        for (const link of links) {
          if (link.href.includes(`/products/${firstHandle}`)) {
            pinnedItem = item;
            break;
          }
        }
        if (pinnedItem) break;
      }

      // If pinned product exists on current page
      if (pinnedItem) {
        grid.insertBefore(pinnedItem, grid.firstChild);
        
        // If variant specified, swap the image
        if (variantID) {
          await swapVariantImage(pinnedItem, firstHandle, variantID);
        }
        
        // Remove excess items if limit specified
        const allItems = Array.from(grid.querySelectorAll('.product-grid__item'));
        if (allItems.length > limit) {
          allItems.slice(limit).forEach(item => item.remove());
        }
        
        updateCount();
        return;
      }

      // Product not on current page - fetch from other pages and reorganize
      await fetchAndReorganizeGrid(firstHandle, variantID, source, limit);
      
    } catch (error) {
      console.error('Curated collection error:', error);
    }
  })();

  /* ------------------------------------------------------------------ */
  /* Helper: Swap variant image on pinned product                      */
  /* ------------------------------------------------------------------ */
  async function swapVariantImage(productItem, handle, variantId) {
    try {
      const prod = await fetch(`/products/${handle}.js`).then(r => r.json());
      const varObj = prod.variants.find(v => String(v.id) === variantId);
      
      if (!varObj || !varObj.featured_image) return;

      const newSrcBase = varObj.featured_image.src;
      
      // Update all images in the pinned product card
      productItem.querySelectorAll('img').forEach(img => {
        const m = img.src.match(/_(\d+x)\./);
        const sized = m ? newSrcBase.replace(/\.(jpg|png|webp)/i, `_${m[1]}.$1`) : newSrcBase;
        img.src = sized;
        img.srcset = '';
      });

      // Update all product links to include variant parameter
      productItem.querySelectorAll('a[href*="/products/"]').forEach(a => {
        const u = new URL(a.href, location.origin);
        u.searchParams.set('variant', variantId);
        a.href = u.pathname + u.search;
      });
    } catch (err) {
      console.error('Error swapping variant image:', err);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Helper: Fetch product from other pages and reorganize             */
  /* ------------------------------------------------------------------ */
  async function fetchAndReorganizeGrid(handle, variantId, sourceCollection, maxProducts) {
    try {
      // Get all handles from the source collection
      const handlesUrl = appendPreview(`/collections/${encodeURIComponent(sourceCollection)}?view=handles.json`);
      const handlesData = await fetch(handlesUrl).then(r => r.json());
      const allHandles = Array.isArray(handlesData?.handles) ? handlesData.handles : [];

      // Build ordered list: pinned first, then others
      const seen = new Set([handle]);
      const orderedHandles = [handle];
      
      for (const h of allHandles) {
        if (h && !seen.has(h)) {
          seen.add(h);
          orderedHandles.push(h);
        }
        if (orderedHandles.length >= maxProducts) break;
      }

      // Get current items from grid
      const currentItems = Array.from(grid.querySelectorAll('.product-grid__item'));
      const itemsByHandle = new Map();
      
      // Map existing items by handle
      currentItems.forEach(item => {
        const links = item.querySelectorAll('a[href*="/products/"]');
        for (const link of links) {
          const match = link.href.match(/\/products\/([^?/#]+)/);
          if (match) {
            itemsByHandle.set(match[1], item);
            break;
          }
        }
      });

      // Fetch missing products (those not on current page)
      const missingHandles = orderedHandles.filter(h => !itemsByHandle.has(h));
      const fetchedItems = await fetchProductCards(missingHandles, sourceCollection);
      
      // Merge fetched items into our map
      fetchedItems.forEach((item, handle) => {
        if (item) itemsByHandle.set(handle, item);
      });

      // Clear and rebuild grid in correct order
      grid.innerHTML = '';
      
      for (const h of orderedHandles) {
        const item = itemsByHandle.get(h);
        if (item) {
          grid.appendChild(item);
          
          // If this is the pinned product and variant specified, swap image
          if (h === handle && variantId) {
            await swapVariantImage(item, handle, variantId);
          }
        }
      }

      updateCount();
      
    } catch (err) {
      console.error('Error reorganizing grid:', err);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Helper: Fetch product cards from collection pages                 */
  /* ------------------------------------------------------------------ */
  async function fetchProductCards(handles, sourceCollection) {
    const itemMap = new Map();
    
    if (handles.length === 0) return itemMap;

    try {
      // First, get all handles from the collection to determine which pages to fetch
      const handlesUrl = appendPreview(`/collections/${encodeURIComponent(sourceCollection)}?view=handles.json`);
      const handlesData = await fetch(handlesUrl).then(r => r.json());
      const allHandles = Array.isArray(handlesData?.handles) ? handlesData.handles : [];
      
      // Determine products per page (check your theme settings - usually 24)
      const productsPerPage = parseInt(qp.get('limit')) || 16;
      
      // Find which pages contain our missing handles
      const pagesToFetch = new Set();
      handles.forEach(handle => {
        const index = allHandles.indexOf(handle);
        if (index !== -1) {
          const pageNum = Math.floor(index / productsPerPage) + 1;
          pagesToFetch.add(pageNum);
        }
      });

      // If we couldn't determine pages, fall back to fetching first few pages
      if (pagesToFetch.size === 0) {
        for (let i = 1; i <= 3; i++) pagesToFetch.add(i);
      }

      // Fetch only the necessary pages
      const fetchPromises = Array.from(pagesToFetch).map(page => {
        const pageUrl = appendPreview(`/collections/${encodeURIComponent(sourceCollection)}?page=${page}`);
        return fetch(pageUrl)
          .then(r => r.text())
          .catch(() => null);
      });

      const pages = await Promise.all(fetchPromises);
      
      // Parse HTML and extract product cards
      pages.forEach(html => {
        if (!html) return;
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const productItems = doc.querySelectorAll('.product-grid__item');
        
        productItems.forEach(item => {
          const links = item.querySelectorAll('a[href*="/products/"]');
          for (const link of links) {
            const match = link.href.match(/\/products\/([^?/#]+)/);
            if (match && handles.includes(match[1])) {
              itemMap.set(match[1], item.cloneNode(true));
              break;
            }
          }
        });
      });

    } catch (err) {
      console.error('Error fetching product cards:', err);
    }

    return itemMap;
  }

  /* ------------------------------------------------------------------ */
  /* Helper: Update product count                                       */
  /* ------------------------------------------------------------------ */
  function updateCount() {
    const count = grid.querySelectorAll('.product-grid__item').length;
    document.querySelectorAll('[data-product-count], .product-count, .collection-product-count')
      .forEach(el => el.textContent = `${count} Products`);
  }
})();